import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';
import Conversion from '../models/Conversion';
import Conversions from '../collections/Conversions';
import Members from '../collections/Members';
import { baseApi } from 'core/api';
import { useOrganizationStore, useUserStore } from 'store/modules';

const state = () => ({
    _analyticsDashboardLoading: true,
    _analyticsDashboardTotals: [],
    _analyticsDashboardInboxes: [],
    _analyticsDashboardTotalInboxes: 0,
    _analyticsDashboardCalls: [],

    _analyticsConversionsStatistic: [],
    _analyticsConversionsStatisticLoading: true,
    _analyticsConversionLoading: true,
    _analyticsConversionLoadingError: false,
    _analyticsConversionItem: null,
    _analyticsConversionItemLoading: true,
    _analyticsConversionChart: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsConversionChartLoading: true,
    _analyticsConversionItems: [],
    _analyticsConversionPagination: {
        per_page: 10,
    },
    _analyticsConversionParameters: {
        page: 1,
        length: 10,
    },

    _analyticParams: {
        from: '',
        to: '',
        filter: {
            members: [],
            inboxes: [],
            numbers: [],
            tags: [],
            sources: [],
        },
        interval: 'day',
    },

    _analyticsOverall: [],
    _analyticsOverallLoading: true,

    _analyticsConversations: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsConversationsLoading: true,

    _analyticsMessages: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsMessagesLoading: true,

    _analyticsCreditUsage: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsCreditUsageLoading: true,
    _analyticsCreditUsageTable: [],
    _analyticsCreditUsageTableLoading: false,
    _analyticsCreditUsageTableLoadingError: false,
    _analyticsCreditUsageTablePagination: {
        per_page: 10,
    },
    _analyticsCreditUsageTableParameters: {
        page: 1,
        limit: 10,
        sortOrder: null,
        sortBy: null,
    },
    _analyticsCreditUsageTableType: 'messages',

    _analyticsCalling: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsCallingLoading: true,

    _analyticsContacts: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsContactsLoading: true,

    _analyticsPerformance: {
        statistic: [],
        charts: {
            labels: [],
            items: [],
        },
    },
    _analyticsPerformanceLoading: true,
    _analyticsPerformanceMembers: [],
    _analyticsPerformanceMembersLoading: false,
    _analyticsPerformanceMembersLoadingError: false,
    _analyticsPerformanceMembersPagination: {
        per_page: 10,
    },
    _analyticsPerformanceMembersParameters: {
        page: 1,
        length: 10,
        sortOrder: null,
        sortBy: null,
    },
});

export const utils = {
    percent: (item) => {
        if (item.value === 0 && item.prevValue === 0) return 0;

        const prevCurrentValue = item.prevValue - item.value;
        const currentValue = item.value;
        const prevValue = item.prevValue;

        return Math.abs(
            (item.isUp ? prevCurrentValue / currentValue : prevCurrentValue / prevValue) * 100
        ).toFixed(0);
    },

    formatDinamic: (milliseconds, type, format = null) => {
        const hoursCustom = Math.floor(milliseconds / 1000 / 60 / 60);
        const minutes = moment(milliseconds).format(format || 'mm');
        const seconds = moment(milliseconds).format('ss');

        if (type === 1) {
            return `${hoursCustom}:${minutes}`;
        }

        if (type === 2) {
            return hoursCustom ? `${hoursCustom}:${minutes}` : `${minutes}:${seconds}`;
        }

        if (type === 3) {
            const duration = moment.duration(milliseconds, 'milliseconds');
            const daysAmount = Math.floor(milliseconds / 1000 / 3600 / 24);
            const hoursAmount = duration.hours();
            const minutesAmount = duration.minutes();

            let result = `${seconds}s`;
            if (minutesAmount) {
                result = `${minutesAmount}`.padStart(2, '0') + 'm ' + result;
            }
            if (hoursAmount) {
                result = `${hoursAmount}h ${result}`;
            }
            if (daysAmount) {
                result = `${daysAmount}d ${result}`;
            }
            return result;
        }

        if (hoursCustom) {
            return `${hoursCustom}:${minutes}:${seconds}`;
        }

        return `${minutes}:${seconds}`;
    },
};

const getters = {
    analyticsDashboardLoading: (state) => state._analyticsDashboardLoading,
    analyticsDashboardTotals: (state) => state._analyticsDashboardTotals,
    analyticsDashboardTotalsForDownload: (state) => {
        return state._analyticsDashboardTotals.map((stat) => {
            let data = {
                total_segments: parseInt(stat.total_segments),
                segments_sent: parseInt(stat.segments_sent),
                segments_received: parseInt(stat.segments_received),
                new_contacts: parseInt(stat.new_contacts),
                opt_in: parseInt(stat.opt_in),
                opt_out: parseInt(stat.opt_out),
            };

            if (stat.hasOwnProperty('hour')) {
                return {
                    date: moment
                        .utc(`${stat.date} ${stat.hour}`, 'YYYY-MM-DD HH')
                        .local()
                        .format('YYYY-MM-DD HH:mm:ss'),
                    ...data,
                };
            } else {
                return {
                    date: moment.utc(stat.date, 'YYYY-MM-DD').local().format('YYYY-MM-DD HH:mm:ss'),
                    ...data,
                };
            }
        });
    },
    analyticsDashboardInboxes: (state) => state._analyticsDashboardInboxes,
    analyticsDashboardTotalInboxes: (state) => state._analyticsDashboardTotalInboxes,
    analyticsDashboardCalls: (state) => state._analyticsDashboardCalls,

    analyticsOverall: (state) => state._analyticsOverall,
    analyticsOverallLoading: (state) => state._analyticsOverallLoading,

    analyticsConversations: (state) => state._analyticsConversations,
    analyticsConversationsStatistic() {
        return this.analyticsConversations.statistic;
    },
    analyticsConversationsCharts() {
        return this.analyticsConversations.charts;
    },
    analyticsConversationsLoading: (state) => state._analyticsConversationsLoading,

    analyticsMessages: (state) => state._analyticsMessages,
    analyticsMessagesStatistic() {
        return this.analyticsMessages.statistic;
    },
    analyticsMessagesCharts() {
        return this.analyticsMessages.charts;
    },
    analyticsMessagesLoading: (state) => state._analyticsMessagesLoading,

    analyticsCreditUsage: (state) => state._analyticsCreditUsage,
    analyticsCreditUsageStatistic() {
        return this.analyticsCreditUsage.statistic;
    },
    analyticsCreditUsageCharts() {
        return this.analyticsCreditUsage.charts;
    },
    analyticsCreditUsageLoading: (state) => state._analyticsCreditUsageLoading,
    analyticsCreditUsageTable: (state) => state._analyticsCreditUsageTable,
    analyticsCreditUsageTableLoading: (state) => state._analyticsCreditUsageTableLoading,
    analyticsCreditUsageTableLoadingError: (state) => state._analyticsCreditUsageTableLoadingError,
    analyticsCreditUsageTablePagination: (state) => state._analyticsCreditUsageTablePagination,
    analyticsCreditUsageTableParameters: (state) => state._analyticsCreditUsageTableParameters,
    analyticsCreditUsageTableType: (state) => state._analyticsCreditUsageTableType,

    analyticsCalling: (state) => state._analyticsCalling,
    analyticsCallingStatistic() {
        return this.analyticsCalling.statistic;
    },
    analyticsCallingCharts() {
        return this.analyticsCalling.charts;
    },
    analyticsCallingLoading: (state) => state._analyticsCallingLoading,

    analyticsContacts: (state) => state._analyticsContacts,
    analyticsContactsStatistic() {
        return this.analyticsContacts.statistic;
    },
    analyticsContactsCharts() {
        return this.analyticsContacts.charts;
    },
    analyticsContactsLoading: (state) => state._analyticsContactsLoading,

    analyticsPerformance: (state) => state._analyticsPerformance,
    analyticsPerformanceStatistic() {
        return this.analyticsPerformance.statistic;
    },
    analyticsPerformanceCharts() {
        return this.analyticsPerformance.charts;
    },
    analyticsPerformanceLoading: (state) => state._analyticsPerformanceLoading,
    analyticsPerformanceMembers: (state) => state._analyticsPerformanceMembers,
    analyticsPerformanceMembersLoading: (state) => state._analyticsPerformanceMembersLoading,
    analyticsPerformanceMembersLoadingError: (state) =>
        state._analyticsPerformanceMembersLoadingError,
    analyticsPerformanceMembersPagination: (state) => state._analyticsPerformanceMembersPagination,
    analyticsPerformanceMembersParameters: (state) => state._analyticsPerformanceMembersParameters,

    analyticsConversionsStatistic: (state) => state._analyticsConversionsStatistic,
    analyticsConversionsStatisticLoading: (state) => state._analyticsConversionsStatisticLoading,
    analyticsConversionLoading: (state) => state._analyticsConversionLoading,
    analyticsConversionLoadingError: (state) => state._analyticsConversionLoadingError,
    analyticsConversionItem: (state) => state._analyticsConversionItem,
    analyticsConversionItemLoading: (state) => state._analyticsConversionItemLoading,
    analyticsConversionItems: (state) => state._analyticsConversionItems,
    analyticsConversionPagination: (state) => state._analyticsConversionPagination,
    analyticsConversionParameters: (state) => state._analyticsConversionParameters,
    analyticsConversionChart: (state) => state._analyticsConversionChart,
    analyticsConversionStatistic() {
        return this.analyticsConversionChart.statistic;
    },
    analyticsConversionCharts() {
        return this.analyticsConversionChart.charts;
    },
    analyticsConversionChartLoading: (state) => state._analyticsConversionChartLoading,

    analyticParams: (state) => state._analyticParams,
};

const actions = {
    async getAnalyticsOverall(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'overall' });

        try {
            const preparedOptions = cloneDeep(options);
            preparedOptions.params = _prepareAnalyticsParams(options.params);

            const newAnalyticsEndpoints =
                useOrganizationStore().featureFlags.new_analytics_endpoints || false;
            const initUrl = newAnalyticsEndpoints
                ? 'analytics/analytics/overall'
                : 'core/analytics/overall';

            const [performance, messages, calls] = await Promise.all([
                baseApi.get(`${initUrl}/performance`, preparedOptions).then((r) => r.data),
                baseApi.get(`${initUrl}/messages`, preparedOptions).then((r) => r.data),
                baseApi.get(`${initUrl}/calls`, preparedOptions).then((r) => r.data),
            ]);

            const data = {
                performance: performance,
                messages: messages,
                calls: calls,
            };

            this._setAnalyticsOverallLoaded(data);
        } finally {
            this._setAnalyticsStateLoading({ status: false, type: 'overall' });
        }
    },

    getAnalyticsConversations(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'conversations' });

        const fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/conversations'
            : 'core/analytics/conversations';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsConversationsLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'conversations' });
            });
    },

    getAnalyticsMessages(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'messages' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/messages'
            : 'core/analytics/messages';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsMessagesLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'messages' });
            });
    },

    getAnalyticsCreditUsage(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'creditUsage' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/credits/usage-totals'
            : 'billing/credits/usage-totals';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsCreditUsageLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'creditUsage' });
            });
    },

    getAnalyticsCalling(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'calling' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/calling'
            : 'core/analytics/calling';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsCallingLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'calling' });
            });
    },

    getAnalyticsContacts(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'contacts' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/contacts'
            : 'core/analytics/contacts';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsContactsLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'contacts' });
            });
    },

    getAnalyticsPerformance(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'performance' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/performance'
            : 'core/analytics/performance';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsPerformanceLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'performance' });
            });
    },

    getAnalyticsPerformanceMembers(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'performance_members' });

        options = {
            params: {
                ..._prepareAnalyticsParams(options.params),
                ...this._analyticsPerformanceMembersParameters,
            },
        };
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/performance/members'
            : 'core/analytics/performance/members';

        return baseApi
            .get(url, options)
            .then((response) => {
                this._setAnalyticsPerformanceMembersLoaded(response.data);
            })
            .catch((e) => {
                this._setAnalyticsPerformanceMembersLoadedError(e);
            })
            .finally(() => {
                this._setAnalyticsStateLoading({
                    status: false,
                    type: 'performance_members',
                });
            });
    },

    async getAnalyticsPerformanceMembersUpdatePage({ page, options }) {
        this._setAnalyticsPerformanceMembersUpdatePage(page);
        await this.getAnalyticsPerformanceMembers({
            params: options,
        });
    },

    async getAnalyticsPerformanceMembersUpdateLength({ length, options }) {
        this._setAnalyticsPerformanceMembersUpdateLength(length);
        await this.getAnalyticsPerformanceMembers({
            params: options,
        });
    },

    getAnalyticsCreditUsageTable(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'creditUsageTable' });

        options = {
            params: {
                object_event_type_group: this._analyticsCreditUsageTableType,
                ..._prepareAnalyticsParams(options.params),
                ...this._analyticsCreditUsageTableParameters,
            },
        };
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/credits/usage-list'
            : 'billing/credits/usage-list';

        return baseApi
            .get(url, options)
            .then((response) => {
                this.getAnalyticsCreditUsageTableDetails({
                    data: response.data,
                });
            })
            .catch((e) => {
                this._setAnalyticsCreditUsageTableLoadedError(e);
                this._setAnalyticsStateLoading({ status: false, type: 'creditUsageTable' });
            });
    },

    getAnalyticsCreditUsageTableDetails({ data }) {
        return baseApi
            .post('core/analytics/credits-usage/details', data.data)
            .then((response) => {
                this._setAnalyticsCreditUsageTableLoaded({
                    data: response.data,
                    meta: data.meta,
                });
            })
            .catch((e) => {
                this._setAnalyticsCreditUsageTableLoadedError(e);
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'creditUsageTable' });
            });
    },

    async getAnalyticsCreditUsageTableUpdatePage({ page, options }) {
        this._setAnalyticsCreditUsageTableUpdatePage(page);
        await this.getAnalyticsCreditUsageTable({
            params: options,
        });
    },

    async getAnalyticsCreditUsageTableUpdateType({ type, options }) {
        this._analyticsCreditUsageTable = [];

        this._setAnalyticsCreditUsageTableUpdateType(type);
        await this.getAnalyticsCreditUsageTable({
            params: options,
        });
    },

    async getAnalyticsCreditUsageTableUpdateLength({ length, options }) {
        this._setAnalyticsCreditUsageTableUpdateLength(length);
        await this.getAnalyticsCreditUsageTable({
            params: options,
        });
    },

    getAnalyticsConversionsStatistic(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'conversions_statistic' });

        let fullOptions = cloneDeep(options);
        if (fullOptions.preload) {
            fullOptions.params['preload_type'] = 'previous';
        }
        fullOptions.params = _prepareAnalyticsParams(fullOptions.params);
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? 'analytics/analytics/conversions'
            : 'core/analytics/conversions';

        return baseApi
            .get(url, fullOptions)
            .then((response) => {
                if (!fullOptions.preload) {
                    this._setAnalyticsConversionsStatisticLoaded(response.data);
                }
            })
            .finally(() => {
                this._setAnalyticsStateLoading({
                    status: false,
                    type: 'conversions_statistic',
                });
            });
    },

    getAnalyticsConversions({ params, isNew = false } = {}) {
        this._setAnalyticsStateLoading({ status: true, type: 'conversions_table' });

        const options = {
            params: {
                ..._prepareAnalyticsParams(params),
                ...this._analyticsConversionParameters,
            },
        };
        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = isNew
            ? newAnalyticsEndpoints
                ? 'analytics/analytics/conversions/list'
                : 'core/analytics/conversions/list'
            : 'core/conversions';

        delete options.params.length;
        delete options.params.page;

        return baseApi
            .get(url, options)
            .then((response) => {
                this._setAnalyticsConversionsLoaded(response.data);

                return response;
            })
            .catch((e) => {
                this._setAnalyticsConversionsLoadedError(e);
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'conversions_table' });
            });
    },

    getAnalyticsConversionsUpdateLength({ length, options, isNew }) {
        this._setAnalyticsConversionsUpdateLength(length);
        return this.getAnalyticsConversions({
            params: options,
            isNew: isNew,
        });
    },

    getAnalyticsConversionsUpdatePage({ page, options, isNew }) {
        this._setAnalyticsConversionsUpdatePage(page);

        return this.getAnalyticsConversions({
            params: options,
            isNew: isNew,
        });
    },

    getAnalyticsConversionsById(id) {
        this._setAnalyticsStateLoading({ status: true, type: 'conversion_item' });

        return baseApi
            .get(`core/conversions/${id}`)
            .then((response) => {
                const conversion = new Conversion(response.data);

                this._setAnalyticsConversionLoaded(conversion);

                return conversion;
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'conversion_item' });
            });
    },

    fetchAnalyticsConversionsByIdChart({ options, id }) {
        this._setAnalyticsStateLoading({ status: true, type: 'conversions_chart' });

        const preparedOptions = cloneDeep(options);
        preparedOptions.params = _prepareAnalyticsParams(preparedOptions.params);

        const newAnalyticsEndpoints =
            useOrganizationStore().featureFlags.new_analytics_endpoints || false;
        const url = newAnalyticsEndpoints
            ? `analytics/analytics/conversions/${id}/chart`
            : `core/analytics/conversions/${id}/chart`;

        return baseApi
            .get(url, preparedOptions)
            .then((response) => {
                this._setAnalyticsConversionChartLoaded(response.data);
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'conversions_chart' });
            });
    },

    createConversions(form) {
        return form.post('core/conversions').then((response) => {
            const conversion = new Conversion(response.data);

            this._setAnalyticsConversionCreate(conversion);

            return response;
        });
    },

    updateConversionsById({ form, id }) {
        return form.put(`core/conversions/${id}`).then((response) => {
            const conversion = new Conversion(response.data);

            this._setAnalyticsConversionUpdate(conversion);

            return response;
        });
    },

    deleteAnalyticsConversionsById(id) {
        return baseApi.delete(`core/conversions/${id}`).then((response) => {
            const conversion = new Conversion(response.data);

            this._setAnalyticsConversionDelete(conversion);

            return response;
        });
    },

    updateConversionsByIdClear(id) {
        return baseApi.post(`core/conversions/${id}/clear`).then((response) => {
            return response;
        });
    },

    updateConversionsByIdVerify(id) {
        return baseApi.post(`core/conversions/${id}/verify`).then((response) => {
            this._setAnalyticsConversionVerify(id);

            return response;
        });
    },

    getOrganizationAnalytics(options) {
        this._setAnalyticsStateLoading({ status: true, type: 'dashboard' });

        const preparedOptions = cloneDeep(options);
        preparedOptions.params = _prepareAnalyticsParams(preparedOptions.params);
        return baseApi
            .get('core/organization/analytics', preparedOptions)
            .then((response) => {
                this._setAnalyticsDashboardLoaded({
                    data: response.data,
                    timezone: options.params.timezone,
                });
                return response.data;
            })
            .finally(() => {
                this._setAnalyticsStateLoading({ status: false, type: 'dashboard' });
            });
    },

    getAnalyticsByTypeExport({ type, params }) {
        return baseApi
            .get(`core/analytics/${type}/export`, {
                params: _prepareAnalyticsParams(params),
            })
            .then((response) => {
                return response.data;
            });
    },

    updateAnalyticsParams(data) {
        this._setAnalyticsUpdateParams(data);
    },

    updateAnalyticsState(data) {
        this._setAnalyticsUpdateState(data);
    },

    sortAnalyticsPerformance({ sortBy, sortOrder, params }) {
        this._setAnalyticsPerformanceMembersUpdateSort({ sortOrder, sortBy });
        this.getAnalyticsPerformanceMembers({ params });
    },

    sortAnalyticsCreditUsageTable({ sortBy, sortOrder, params }) {
        this._setAnalyticsCreditUsageTableUpdateSort({ sortOrder, sortBy });
        this.getAnalyticsCreditUsageTable({ params });
    },

    _setAnalyticsUpdateState(data) {
        for (let key in data) {
            const mappedKey = key.startsWith('_') ? key : `_${key}`;
            this[mappedKey] = data[key];
        }
    },

    _setAnalyticsUpdateParams(data) {
        this._analyticParams = {
            ...this._analyticParams,
            ...data,
        };
    },

    _setAnalyticsStateLoading({ status, type }) {
        if (type === 'overall') {
            this._analyticsOverallLoading = status;
        }

        if (type === 'conversations') {
            this._analyticsConversationsLoading = status;
        }

        if (type === 'messages') {
            this._analyticsMessagesLoading = status;
        }

        if (type === 'creditUsage') {
            this._analyticsCreditUsageLoading = status;
        }

        if (type === 'creditUsageTable') {
            this._analyticsCreditUsageTableLoading = status;
        }

        if (type === 'calling') {
            this._analyticsCallingLoading = status;
        }

        if (type === 'contacts') {
            this._analyticsContactsLoading = status;
        }

        if (type === 'performance') {
            this._analyticsPerformanceLoading = status;
        }

        if (type === 'performance_members') {
            this._analyticsPerformanceMembersLoading = status;
        }

        if (type === 'conversions_statistic') {
            this._analyticsConversionsStatisticLoading = status;
        }

        if (type === 'conversions_table') {
            this._analyticsConversionLoading = status;
        }

        if (type === 'conversions_chart') {
            this._analyticsConversionChartLoading = status;
        }

        if (type === 'conversion_item') {
            this._analyticsConversionItemLoading = status;
        }

        if (type === 'dashboard') {
            this._analyticsDashboardLoading = status;
        }
    },

    _setAnalyticsOverallLoaded(response) {
        this._analyticsOverall = [];

        for (let key in response) {
            const items = response[key].map((item) => {
                if (key === 'performance') {
                    if (item.key === 'first_response') item.title = 'Avg. Time to First Response';
                    if (item.key === 'response') item.title = 'Avg. Time to Response';
                    if (item.key === 'close') item.title = 'Avg. Time to Close';

                    item.valueTime = utils.formatDinamic(item.value * 1000, 3);
                }

                if (key === 'messages') {
                    if (item.key === 'total') {
                        item.title = 'Total Messages';
                        item.tooltip = 'The total number of sent & received messages';
                    }
                    if (item.key === 'inbound') item.title = 'Inbound Messages';
                    if (item.key === 'outbound') item.title = 'Outbound Messages';
                }

                if (key === 'calls') {
                    if (item.key === 'total') item.title = 'Total Calls';
                    if (item.key === 'inbound') item.title = 'Inbound Calls';
                    if (item.key === 'outbound') item.title = 'Outbound Calls';
                }

                item.uuid = uuidv4();
                item.labels = !item.data.length
                    ? [0, 0]
                    : item.data.length === 1
                      ? [item.data[0], item.data[0]]
                      : item.data;
                item.items = [
                    {
                        label: '',
                        data: !item.data.length
                            ? [0, 0]
                            : item.data.length === 1
                              ? [item.data[0], item.data[0]]
                              : item.data,
                    },
                ];

                delete item.data;

                return item;
            });

            const section = {
                key: key,
                items: items,
            };

            if (key === 'performance') section.title = 'Performance';
            if (key === 'messages') section.title = 'Messages';
            if (key === 'calls') section.title = 'Calls';

            this._analyticsOverall.push(section);
        }
    },

    _setAnalyticsConversationsLoaded(response) {
        const sort = {
            total: 0,
            open: 1,
            closed: 2,
        };
        const statisticKeys = ['total', 'open', 'closed'];
        const chartsKeys = ['new'];

        const statistic = response.statistic
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.isPercent = false;
                item.tooltip = '';
                item.disabled = true;

                if (item.key === 'total') {
                    item.isPercent = true;
                }
                if (item.key === 'new') {
                    item.color = '#3283E0';
                    item.disabled = false;
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsConversations.statistic = statistic.filter(
            (item) => !item.parent && statisticKeys.includes(item.key)
        );

        this._analyticsConversations.charts = {
            labels: response.charts
                .filter((item) => {
                    let shouldBeAdded = false;
                    chartsKeys.forEach((key) => {
                        // we should be added value to chart if at least one of needed keys has value
                        shouldBeAdded = shouldBeAdded || item[key];
                    });
                    return shouldBeAdded;
                })
                .map((item) => item.date),
            items: statistic
                .filter((item) => chartsKeys.includes(item.key))
                .map((item) => {
                    return {
                        key: item.key,
                        parent: null,
                        data: response.charts.map((c) => Math.round(c[item.key])),
                        label: item.label,
                        color: item.color,
                        disabled: item.disabled,
                        yAxisID: 'y',
                    };
                }),
        };
    },

    _setAnalyticsCreditUsageLoaded(response) {
        const sort = {
            total_credits: 0,
            messages_credits: 1,
            calls_credits: 2,
            chatbots_credits: 3,
            transcripts_credits: 4,
            integrations_credits: 5,
        };

        const statistic = response.statistic
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = false;
                item.color = '#c5ced8';
                item.isHiddenValue = true;

                if (item.key === 'total_credits') {
                    item.floatNumber = true;
                    item.color = '#13B763';
                    item.hidden = true;
                    item.disabledGlobal = true;
                }

                if (item.key === 'messages_credits') {
                    item.floatNumber = true;
                    item.color = '#3283E0';
                    item.tooltip =
                        '1 credit per SMS segment, 2 credits per MMS. <a href="https://help.salesmessage.com/en/articles/4697613-how-long-can-a-text-message-be" target="_blank">Learn more.</a>';
                }

                if (item.key === 'calls_credits') {
                    item.color = '#5BA1F1';
                }

                if (item.key === 'chatbots_credits') {
                    item.color = '#84C1FA';
                }

                if (item.key === 'transcripts_credits') {
                    item.color = '#B4DBFF';
                }

                if (item.key === 'integrations_credits') {
                    item.label = 'Salesforce Credits';
                    item.color = '#C5CED8';
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsCreditUsage.statistic = statistic.filter((item) => !item.parent);

        this._analyticsCreditUsage.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                return {
                    key: item.key,
                    parent: item.parent,
                    data: response.charts.map((c) => Math.round(c[item.key])),
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: 'y',
                };
            }),
        };
    },

    _setAnalyticsMessagesLoaded(response) {
        const sort = {
            total_inbound: 0,
            total_outbound: 1,
            delivered: 2,
            undelivered: 3,
            carrier_violations: 4,
            optouts: 5,
        };

        const totalOutbound = response.statistic.find((item) => item.key === 'total_outbound');

        const statistic = response.statistic
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = false;

                if (item.key === 'total_inbound') {
                    item.color = '#2AC990';
                    item.tooltip = 'The total number of inbound messages';
                }
                if (item.key === 'total_outbound') {
                    item.color = '#3283E0';
                    item.hasToggle = true;
                    item.flex = '2 0 0';
                    item.tooltip = 'The total number of outbound messages';
                }
                if (item.key === 'delivered') {
                    item.color = '#5BA1F1';
                    item.label = 'Delivery rate';
                    item.valuePercent = Math.round((item.value / totalOutbound.value) * 100);
                    item.disabled = true;
                    item.tooltip =
                        'The percentage of delivered messages. <a href="https://help.salesmessage.com/en/articles/4104691-new-analytics-reporting" target="_blank">Learn more</a>';
                }
                if (item.key === 'undelivered') {
                    item.color = '#84C1FA';
                    item.disabled = true;
                    item.label = 'Error rate';
                    item.valuePercent = Math.round((item.value / totalOutbound.value) * 100);
                    item.tooltip =
                        'The percentage of undelivered messages. <a href="https://help.salesmessage.com/en/articles/4104691-new-analytics-reporting" target="_blank">Learn more</a>';
                }
                if (item.key === 'carrier_violations') {
                    item.color = '#B4DBFF';
                    item.disabled = true;
                    item.label = 'Spam rate';

                    const percents = (item.value / totalOutbound.value) * 100;

                    item.valuePercent = percents < 2 ? percents.toFixed(2) : Math.round(percents);
                    item.tooltip =
                        'The percentage of undelivered messages that were flagged for carrier violations. <a href="https://help.salesmessage.com/en/articles/4104691-new-analytics-reporting" target="_blank">Learn more</a>';
                }
                if (item.key === 'optouts') {
                    item.isUp = item.value < item.prevValue;
                    item.color = '#C5CED8';
                    item.label = 'Opted-out';
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsMessages.statistic = statistic.filter((item) => !item.parent);

        this._analyticsMessages.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                return {
                    key: item.key,
                    parent: item.parent,
                    data: response.charts.map((c) => Math.round(c[item.key])),
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: 'y',
                };
            }),
        };
    },

    _setAnalyticsCallingLoaded(response) {
        const sort = {
            outbound_calls: 0,
            answered_outbound: 1,
            unanswered_outbound: 2,
            failed_outbound: 3,
            busy_outbound: 4,
            avg_duration_outbound: 5,
            inbound_calls: 6,
            answered_inbound: 7,
            missed_inbound: 8,
            failed_inbound: 9,
            busy_inbound: 10,
            avg_duration_inbound: 11,
            forwarded: 12,
            avg_duration_forwarded: 13,
        };

        const statistic = response.statistic
            .filter((item) => item.key !== 'total')
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = false;

                if (item.key === 'total') {
                    item.color = '#7A7F91';
                }
                if (item.key === 'outbound_calls') {
                    item.flex = '2.5 0 0';
                    item.color = '#1F986D';
                }
                if (item.key === 'avg_duration_outbound') {
                    item.disabled = true;
                    item.disabledClick = true;
                    item.labelUpdate = 'Avg. Duration';
                    item.valueTime = moment(item.value * 60 * 1000).format('m:ss');
                    item.color = '#B3EDD8';
                    item.tooltip = 'Time format is minutes:seconds.';
                }
                if (item.key === 'answered_outbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Answered';
                    item.color = '#25B380';
                }
                if (item.key === 'unanswered_outbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Unanswered';
                    item.color = '#2AC990';
                }
                if (item.key === 'failed_outbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Failed';
                    item.color = '#55E0B6';
                }
                if (item.key === 'busy_outbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Busy';
                    item.color = '#91E7C8';
                }
                if (item.key === 'inbound_calls') {
                    item.flex = '2.5 0 0';
                    item.color = '#1F5898';
                }
                if (item.key === 'avg_duration_inbound') {
                    item.disabled = true;
                    item.disabledClick = true;
                    item.labelUpdate = 'Avg. Duration';
                    item.valueTime = moment(item.value * 60 * 1000).format('m:ss');
                    item.color = '#B4DBFF';
                    item.tooltip = 'Time format is minutes:seconds.';
                }
                if (item.key === 'answered_inbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Answered';
                    item.color = '#286EBE';
                }
                if (item.key === 'missed_inbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Missed';
                    item.color = '#3283E0';
                }

                if (item.key === 'failed_inbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Failed';
                    item.color = '#5BA1F1';
                }
                if (item.key === 'busy_inbound') {
                    item.disabled = true;
                    item.labelUpdate = 'Busy';
                    item.color = '#84C1FA';
                }
                if (item.key === 'forwarded') {
                    item.color = '#7A7F91';
                }
                if (item.key === 'avg_duration_forwarded') {
                    item.disabled = true;
                    item.disabledClick = true;
                    item.labelUpdate = 'Avg. Duration';
                    item.valueTime = moment(item.value * 60 * 1000).format('m:ss');
                    item.color = '#C5CED8';
                    item.tooltip = 'Time format is minutes:seconds.';
                    item.tooltipPlacement = 'top-end';
                }
                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsCalling.statistic = statistic.filter((item) => !item.parent);

        this._analyticsCalling.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                const yAxisID =
                    item.key === 'avg_duration_inbound' ||
                    item.key === 'avg_duration_outbound' ||
                    item.key === 'avg_duration_forwarded'
                        ? 'y1'
                        : 'y';
                const data =
                    item.key === 'avg_duration_inbound' ||
                    item.key === 'avg_duration_outbound' ||
                    item.key === 'avg_duration_forwarded'
                        ? response.charts.map((c) => c[item.key] * 60 * 1000)
                        : response.charts.map((c) => Math.round(c[item.key]));

                return {
                    key: item.key,
                    parent: item.parent,
                    data: data,
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: yAxisID,
                };
            }),
        };
    },

    _setAnalyticsContactsLoaded(response) {
        const statistic = response.statistic
            .filter((item) => item.key !== 'active_contacts')
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = false;
                item.isPercent = true;

                if (item.key === 'new_contacts') {
                    item.color = '#3283E0';
                    item.tooltip =
                        'The total number of contacts that<br /> were created during the selected<br /> date range.';
                }
                if (item.key === 'active_contacts') {
                    item.color = '#5BA1F1';
                }
                if (item.key === 'opted_out') {
                    item.isUp = item.value < item.prevValue;
                    item.color = '#C5CED8';
                    item.tooltip =
                        'The total number of contacts that<br /> opted-out of receiving text messages';
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsContacts.statistic = statistic.filter((item) => !item.parent);

        this._analyticsContacts.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                return {
                    key: item.key,
                    data: response.charts.map((c) => Math.round(c[item.key])),
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: 'y',
                };
            }),
        };
    },

    _setAnalyticsPerformanceLoaded(response) {
        const statistic = response.statistic.map((item, i, array) => {
            item.isUp = item.value > item.prevValue;
            item.conversionPercent = utils.percent(item);
            item.conversionValue = Math.abs(item.value - item.prevValue);
            item.tooltip = '';
            item.disabled = false;
            item.valueTime = utils.formatDinamic(item.value * 1000, 3);

            if (item.key === 'avg_time_to_first_response') {
                item.color = '#3283E0';
                item.isPercent = true;
                item.tooltip =
                    'The average time between the first<br/> inbound message and the first<br/> response.<br/> Time format is days:hours:minutes:seconds.<br/> <a href="https://help.salesmessage.com/en/articles/4104691-analytics-reporting" target="_blank">Learn more.</a>';
            }
            if (item.key === 'avg_time_to_response') {
                item.color = '#5BA1F1';
                item.isPercent = true;
                item.tooltip =
                    'The average time between inbound<br/> and outbound messages for open<br/> conversations. <a href="https://help.salesmessage.com/en/articles/4104691-analytics-reporting" target="_blank">Learn more.</a>';
            }
            if (item.key === 'avg_time_to_close') {
                item.color = '#84C1FA';
                item.isPercent = true;
                item.tooltip =
                    'The average time between the first<br/> inbound message and the time a<br/> conversation is marked as closed.<br/> <a href="https://help.salesmessage.com/en/articles/4104691-analytics-reporting" target="_blank">Learn more.</a>';
            }

            item.items = array.filter((c) => c.parent === item.key);

            return item;
        });

        this._analyticsPerformance.statistic = statistic.filter((item) => !item.parent);

        this._analyticsPerformance.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                return {
                    key: item.key,
                    data: response.charts.map((c) => c[item.key] * 1000),
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: 'y',
                };
            }),
        };
    },

    _setAnalyticsPerformanceMembersLoaded(data) {
        this._analyticsPerformanceMembers = new Members(data.data).models;
        this._analyticsPerformanceMembersPagination = {
            type: '',
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            next_page_url: data.next_page_url,
        };
    },

    _setAnalyticsPerformanceMembersLoadedError() {
        this._analyticsPerformanceMembers = false;
        this._analyticsPerformanceMembersPagination.total = 0;
    },

    _setAnalyticsPerformanceMembersUpdatePage(page) {
        this._analyticsPerformanceMembersParameters.page = page;
    },

    _setAnalyticsPerformanceMembersUpdateLength(length) {
        this._analyticsPerformanceMembersParameters.length = length;
        this._analyticsPerformanceMembersParameters.page = 1;
    },

    _setAnalyticsCreditUsageTableLoaded(data) {
        this._analyticsCreditUsageTable = data.data;
        this._analyticsCreditUsageTablePagination = {
            type: '',
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.last_page,
            per_page: data.meta.per_page,
            to: data.meta.to,
            total: data.meta.total,
            next_page_url: data.meta.next_page_url,
        };
    },

    _setAnalyticsCreditUsageTableLoadedError() {
        this._analyticsCreditUsageTable = false;
        this._analyticsCreditUsageTablePagination.total = 0;
    },

    _setAnalyticsCreditUsageTableUpdatePage(page) {
        this._analyticsCreditUsageTableParameters.page = page;
    },

    _setAnalyticsCreditUsageTableUpdateType(type) {
        this._analyticsCreditUsageTableType = type;
        this._analyticsCreditUsageTableParameters = {
            page: 1,
            limit: 10,
            sortOrder: null,
            sortBy: null,
        };
    },

    _setAnalyticsCreditUsageTableUpdateLength(limit) {
        this._analyticsCreditUsageTableParameters.limit = limit;
        this._analyticsCreditUsageTableParameters.page = 1;
    },

    _setAnalyticsConversionsStatisticLoaded(response) {
        const sort = {
            total: 0,
            avg_conversion_rate: 1,
            total_conversion_revenue: 2,
        };

        this._analyticsConversionsStatistic = response.statistic
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = true;
                item.disabledGlobal = true;
                item.isPercent = true;

                if (item.key === 'total') {
                    item.label = 'Total Conversions';
                }

                if (item.key === 'avg_conversion_rate') {
                    item.valuePercent = `${item.value}%`;
                }

                if (item.key === 'total_conversion_revenue') {
                    item.valueCurrency = `$${item.value}`;
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });
    },

    _setAnalyticsConversionsLoaded(data) {
        const items = Array.isArray(data.data) ? data.data : [];
        const pagination = data.meta;

        this._analyticsConversionItems = new Conversions(items).models;

        this._analyticsConversionPagination.current_page = pagination.current_page;
        this._analyticsConversionPagination.last_page = pagination.last_page;
        this._analyticsConversionPagination.from = pagination.from;
        this._analyticsConversionPagination.to = pagination.to;
        this._analyticsConversionPagination.total = pagination.total;
        this._analyticsConversionPagination.per_page = pagination.per_page;
    },

    _setAnalyticsConversionsLoadedError() {
        this._analyticsConversionItems = false;
        this._analyticsConversionPagination.total = 0;
    },

    _setAnalyticsConversionsUpdatePage(page) {
        this._analyticsConversionParameters.page = page;
    },

    _setAnalyticsConversionsUpdateLength(length) {
        this._analyticsConversionParameters.page = 1;
        this._analyticsConversionParameters.length = length;
    },

    _setAnalyticsConversionLoaded(item) {
        this._analyticsConversionItem = item;
    },

    _setAnalyticsConversionCreate(item) {},

    _setAnalyticsConversionUpdate(item) {},

    _setAnalyticsConversionDelete(item) {},

    _setAnalyticsConversionVerify(id) {},

    _setAnalyticsConversionChartLoaded(response) {
        const sort = {
            conversion_value: 0,
            total: 1,
            conversion_rate: 2,
        };

        const statistic = response.statistic
            .sort((a, b) => {
                const aValue = sort[a.key];
                const bValue = sort[b.key];
                return aValue - bValue;
            })
            .map((item, i, array) => {
                item.isUp = item.value > item.prevValue;
                item.conversionPercent = utils.percent(item);
                item.conversionValue = Math.abs(item.value - item.prevValue);
                item.tooltip = '';
                item.disabled = false;
                item.isPercent = true;

                if (item.key === 'total') {
                    item.color = '#3283E0';
                    item.label = 'Conversions';
                }

                if (item.key === 'conversion_rate') {
                    item.color = '#5BA1F1';
                    item.disabledGlobal = true;
                    item.disabled = true;
                    item.valuePercent = `${item.value}%`;
                }

                if (item.key === 'conversion_value') {
                    item.label = 'Conversions Revenue';
                    item.color = '#84C1FA';
                    item.valueCurrency = `$${item.value}`;
                }

                item.items = array.filter((c) => c.parent === item.key);

                return item;
            });

        this._analyticsConversionChart.statistic = statistic.filter((item) => !item.parent);

        this._analyticsConversionChart.charts = {
            labels: response.charts.map((item) => item.date),
            items: statistic.map((item) => {
                const yAxisID = item.key === 'conversion_value' ? 'y1' : 'y';

                return {
                    key: item.key,
                    data: response.charts.map((c) => Math.round(c[item.key] * 100) / 100),
                    label: item.label,
                    color: item.color,
                    disabled: item.disabled,
                    yAxisID: yAxisID,
                };
            }),
        };
    },

    _setAnalyticsDashboardLoaded({ data, timezone }) {
        this._analyticsDashboardTotals = data.totals.map((stat) => {
            let data = {
                ...stat,
                total_segments: parseInt(stat.total_segments),
                segments_sent: parseInt(stat.segments_sent),
                segments_received: parseInt(stat.segments_received),
                new_contacts: parseInt(stat.total_contacts),
                opt_in: parseInt(stat.contacts_opt_in),
                opt_out: parseInt(stat.contacts_opt_out),
            };

            if (stat.hasOwnProperty('hour')) {
                return {
                    ...data,
                    label: moment
                        .utc(`${stat.date} ${stat.hour}`, 'YYYY-MM-DD HH')
                        .tz(timezone)
                        .format('ha'),
                };
            } else {
                return {
                    ...data,
                    label: moment.utc(stat.date, 'YYYY-MM-DD').tz(timezone).format('MMM D'),
                };
            }
        });

        this._analyticsDashboardInboxes = Object.values(data.inboxes.data);
        this._analyticsDashboardTotalInboxes = data.inboxes.total;
        this._analyticsDashboardCalls = data.calls;
    },

    _setAnalyticsPerformanceMembersUpdateSort({ sortOrder, sortBy }) {
        this._analyticsPerformanceMembersParameters.sortOrder = sortOrder;
        this._analyticsPerformanceMembersParameters.sortBy = sortBy;
    },

    _setAnalyticsCreditUsageTableUpdateSort({ sortOrder, sortBy }) {
        this._analyticsCreditUsageTableParameters.sortOrder = sortOrder;
        this._analyticsCreditUsageTableParameters.sortBy = sortBy;
    },
};

const _prepareAnalyticsParams = (params) => {
    const { user } = useUserStore();

    return {
        ...params,
        timezone: user.timezone,
    };
};

export default {
    state,
    getters,
    actions,
};
